import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@monksflow/monks-ui';
import { Plus } from 'lucide-react';
import { PERMISSIONS } from '@inference.engine/permissions/web';
import { ScopedPermission } from '@/shared/ui';
import { SearchInput } from '@/shared/ui/SearchInput';
import { closeAdaptationPicker, useAdaptationPickerOpen } from '@/shared/hooks';
import { openAssetPlanner, openNewProject } from '../../lib/openAssetPlanner';
import { useAdaptationProjects } from '../../lib/useAdaptationProjects';
import { SelectProjectTable } from '../SelectProjectTable/SelectProjectTable';
import { useTranslation } from 'react-i18next';

/**
 * Global "Select a project" modal for the Adaptation tool. Mounted once as a
 * sibling of <App/> (see main.tsx); reads open state from the shared
 * adaptation-picker store so any Adaptation card can trigger it without a
 * feature → widget/feature import.
 */
export function AdaptationProjectPickerModal() {
  const { t } = useTranslation('projects');

  const isOpen = useAdaptationPickerOpen();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeAdaptationPicker()}>
      {/* The search row scrolls with the list on purpose: Figma's Desktop Scrollable
          pins Header and Footer only, so do not lift it into DialogHeader. */}
      <DialogContent scrollable>
        <DialogHeader>
          <DialogTitle>
            {t('adaptationProjectPicker.adaptationProjectPickerModal.selectAProject')}
          </DialogTitle>
          <DialogDescription>
            {t('adaptationProjectPicker.adaptationProjectPickerModal.chooseAProjectToUse')}
          </DialogDescription>
        </DialogHeader>
        {/* Radix unmounts DialogContent children while closed, so the body
            (and its query) only mounts once the modal is open. */}
        {isOpen && <PickerBody />}
      </DialogContent>
    </Dialog>
  );
}

function PickerBody() {
  const { t } = useTranslation('projects');

  const { rows, workspaceId, isLoading, isError, search, setSearch, hasSearch } =
    useAdaptationProjects();

  const handleSelect = (platformProjectId: string) => {
    if (!workspaceId) return;
    openAssetPlanner(workspaceId, platformProjectId);
    closeAdaptationPicker();
  };

  const handleCreate = () => {
    openNewProject();
    closeAdaptationPicker();
  };

  return (
    <DialogBody className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <SearchInput
          className="w-80"
          placeholder={t('adaptationProjectPicker.adaptationProjectPickerModal.search')}
          aria-label={t('adaptationProjectPicker.adaptationProjectPickerModal.searchProjectsLabel')}
          value={search}
          onChange={setSearch}
        />

        <ScopedPermission scope="workspace" permissions={[PERMISSIONS.ProjectCreate]}>
          <Button size="default" onClick={handleCreate}>
            <Plus className="size-4" />
            {t('adaptationProjectPicker.adaptationProjectPickerModal.createNewProject')}
          </Button>
        </ScopedPermission>
      </div>

      {isError ? (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {t('adaptationProjectPicker.adaptationProjectPickerModal.failedToLoadProjectsPlease')}
        </p>
      ) : isLoading ? (
        <div className="flex flex-col gap-3" data-testid="picker-loading">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full rounded" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {hasSearch
            ? t('adaptationProjectPicker.adaptationProjectPickerModal.noProjectsMatchYourSearch')
            : t('adaptationProjectPicker.adaptationProjectPickerModal.noProjectsFound')}
        </p>
      ) : (
        <SelectProjectTable rows={rows} onSelect={handleSelect} />
      )}
    </DialogBody>
  );
}
